// Cross-domain hreflang mapping for international SEO.
//
// Google uses rel="alternate" hreflang to serve the RIGHT localized version
// to a user based on their country/language. Without cross-domain hreflang,
// Google can't tell a UK user to land on orthocarerent.com/en/ while a PT
// user lands on orthocare.pt — both pages would compete and cannibalize.
//
// Domain intent:
//   Rental content  → orthocare.* / orthocarerent.* domains
//   Sales content   → gestosageis.* domains
//
// Language → domain routing for RENTAL content:
//   pt  → orthocare.pt            (Portugal primary rental hub)
//   en  → orthocarerent.com       (global rental hub for inbound travelers)
//   es  → orthocarerent.com/es/   (Spain → global domain, ES path)
//   fr  → orthocarerent.com/fr/   (France → global domain, FR path)
//   de  → orthocarerent.com/de/   (Germany → global domain, DE path)
//   it  → orthocarerent.com/it/   (Italy → global domain, IT path)
//   nl  → orthocarerent.com/nl/   (Netherlands → global domain, NL path)
//
// For the .pt/.com split on gestosageis, sales content routes:
//   pt  → gestosageis.pt          (Portugal brand hub)
//   en  → gestosageis.com         (global e-commerce)
//   es/fr/de/it/nl → gestosageis.com/{lang}/  (global domain, localized path)

import { LANG_ROUTES } from '@/components/seo/langRoutes';
import { SHOP_LANG_ROUTES } from '@/components/seo/shopLangRoutes';

// SELF-REFERENCING hreflang — each domain points to its OWN language paths,
// NOT cross-domain. This keeps all 6 domains independently indexable (no
// cross-domain canonicals or redirects that would consolidate authority).
// The current domain is resolved at runtime via siteBaseUrl() so every
// domain's hreflang entries stay on the same hostname.
import { siteBaseUrl } from '@/lib/siteDomain';

// Language keys are kept so Object.keys() iteration still works in the
// hreflang builders below. The domain value is resolved dynamically at
// call time — every language resolves to the CURRENT domain, so hreflang
// is self-referencing per domain (language encoded in the URL path, not
// the hostname).
const LANG_KEYS = ['pt', 'en', 'es', 'fr', 'de', 'it', 'nl'];
const RENTAL_DOMAINS = Object.fromEntries(LANG_KEYS.map((l) => [l, l]));
const SALES_DOMAINS = Object.fromEntries(LANG_KEYS.map((l) => [l, l]));

// Resolve any language key to the current domain (self-referencing).
const _domain = () => siteBaseUrl();

// Full hreflang locale codes per language (includes regional variants so
// Google can match country-specific searches like "en-GB", "de-AT", etc.).
const HREFLANG_LOCALES = {
  pt: ['pt-PT', 'pt'],
  en: ['en-GB', 'en-US', 'en-IE', 'en'],
  es: ['es-ES', 'es'],
  fr: ['fr-FR', 'fr-BE', 'fr'],
  de: ['de-DE', 'de-AT', 'de-CH', 'de'],
  it: ['it-IT', 'it'],
  nl: ['nl-NL', 'nl-BE', 'nl'],
};

// Build the cross-domain canonical URL for a rental equipment page.
// `equipmentSlug` is the PT-based stable slug; `countySlug` optional.
export function rentalEquipmentUrl(lang, equipmentSlug, countySlug = '') {
  const cfg = LANG_ROUTES[lang] || LANG_ROUTES.pt;
  const domain = _domain();
  if (!cfg.seg) {
    return `${domain}/aluguer/${equipmentSlug}${countySlug ? `/${countySlug}` : ''}`;
  }
  return `${domain}/${cfg.seg}/${cfg.rentWord}/${equipmentSlug}${countySlug ? `/${countySlug}` : ''}`;
}

// Build the cross-domain canonical URL for a rental district page.
export function rentalDistrictUrl(lang, districtSlug) {
  const cfg = LANG_ROUTES[lang] || LANG_ROUTES.pt;
  const domain = _domain();
  if (!cfg.seg) {
    return `${domain}/aluguer/distrito/${districtSlug}`;
  }
  return `${domain}/${cfg.seg}/${cfg.rentWord}/${cfg.districtWord}/${districtSlug}`;
}

// Build the cross-domain canonical URL for a rental county page.
export function rentalCountyUrl(lang, countySlug) {
  const cfg = LANG_ROUTES[lang] || LANG_ROUTES.pt;
  const domain = _domain();
  if (!cfg.seg) {
    return `${domain}/aluguer/${cfg.countyWord}/${countySlug}`;
  }
  return `${domain}/${cfg.seg}/${cfg.rentWord}/${cfg.countyWord}/${countySlug}`;
}

// Build the cross-domain canonical URL for a sales product page.
// Uses language-specific path segments (loja/shop/tienda/boutique/laden).
export function salesProductUrl(lang, productSlug) {
  const domain = _domain();
  const cfg = SHOP_LANG_ROUTES[lang] || SHOP_LANG_ROUTES.pt;
  if (!cfg.seg) return `${domain}/loja/produto/${productSlug}`;
  return `${domain}/${cfg.seg}/${cfg.shopWord}/${cfg.productWord}/${productSlug}`;
}

// Build the cross-domain canonical URL for a sales category page.
export function salesCategoryUrl(lang, categorySlug) {
  const domain = _domain();
  const cfg = SHOP_LANG_ROUTES[lang] || SHOP_LANG_ROUTES.pt;
  if (!cfg.seg) return `${domain}/loja/${categorySlug}`;
  return `${domain}/${cfg.seg}/${cfg.shopWord}/${categorySlug}`;
}

// Build the cross-domain canonical URL for a sales brand page.
export function salesBrandUrl(lang, brandSlug) {
  const domain = _domain();
  const cfg = SHOP_LANG_ROUTES[lang] || SHOP_LANG_ROUTES.pt;
  if (!cfg.seg) return `${domain}/loja/marca/${brandSlug}`;
  return `${domain}/${cfg.seg}/${cfg.shopWord}/${cfg.brandWord}/${brandSlug}`;
}

// Generate the full set of hreflang link entries for a rental equipment page.
// Returns an array of { hreflang, href } objects covering all 7 languages
// (with regional variants) + x-default.
export function rentalEquipmentHreflang(equipmentSlug, countySlug = '') {
  const entries = [];
  for (const lang of Object.keys(RENTAL_DOMAINS)) {
    const href = rentalEquipmentUrl(lang, equipmentSlug, countySlug);
    for (const locale of HREFLANG_LOCALES[lang]) {
      entries.push({ hreflang: locale, href });
    }
  }
  // x-default → English version on the global rental domain
  entries.push({ hreflang: 'x-default', href: rentalEquipmentUrl('en', equipmentSlug, countySlug) });
  return entries;
}

// Generate the full set of hreflang link entries for a rental district page.
export function rentalDistrictHreflang(districtSlug) {
  const entries = [];
  for (const lang of Object.keys(RENTAL_DOMAINS)) {
    const href = rentalDistrictUrl(lang, districtSlug);
    for (const locale of HREFLANG_LOCALES[lang]) {
      entries.push({ hreflang: locale, href });
    }
  }
  entries.push({ hreflang: 'x-default', href: rentalDistrictUrl('en', districtSlug) });
  return entries;
}

// Generate the full set of hreflang link entries for a rental county page.
export function rentalCountyHreflang(countySlug) {
  const entries = [];
  for (const lang of Object.keys(RENTAL_DOMAINS)) {
    const href = rentalCountyUrl(lang, countySlug);
    for (const locale of HREFLANG_LOCALES[lang]) {
      entries.push({ hreflang: locale, href });
    }
  }
  entries.push({ hreflang: 'x-default', href: rentalCountyUrl('en', countySlug) });
  return entries;
}

// Generate the full set of hreflang link entries for a sales product page.
export function salesProductHreflang(productSlug) {
  const entries = [];
  for (const lang of Object.keys(SALES_DOMAINS)) {
    const href = salesProductUrl(lang, productSlug);
    for (const locale of HREFLANG_LOCALES[lang]) {
      entries.push({ hreflang: locale, href });
    }
  }
  entries.push({ hreflang: 'x-default', href: salesProductUrl('en', productSlug) });
  return entries;
}

// Generate the full set of hreflang link entries for a sales category page.
export function salesCategoryHreflang(categorySlug) {
  const entries = [];
  for (const lang of Object.keys(SALES_DOMAINS)) {
    const href = salesCategoryUrl(lang, categorySlug);
    for (const locale of HREFLANG_LOCALES[lang]) {
      entries.push({ hreflang: locale, href });
    }
  }
  entries.push({ hreflang: 'x-default', href: salesCategoryUrl('en', categorySlug) });
  return entries;
}

// Generate the full set of hreflang link entries for a sales brand page.
export function salesBrandHreflang(brandSlug) {
  const entries = [];
  for (const lang of Object.keys(SALES_DOMAINS)) {
    const href = salesBrandUrl(lang, brandSlug);
    for (const locale of HREFLANG_LOCALES[lang]) {
      entries.push({ hreflang: locale, href });
    }
  }
  entries.push({ hreflang: 'x-default', href: salesBrandUrl('en', brandSlug) });
  return entries;
}

// Pick the canonical URL for the current language from a set of hreflang entries.
export function canonicalFromHreflang(entries, currentLang) {
  const match = entries.find((e) => e.hreflang === currentLang);
  if (match) return match.href;
  const xdef = entries.find((e) => e.hreflang === 'x-default');
  return xdef ? xdef.href : (entries[0]?.href || '');
}